'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
}

const faqs: FAQ[] = [
  {
    id: '1',
    question: 'What is your average response time for support requests?',
    answer: 'For emergency support, we guarantee initial response within 15 minutes. Standard support requests receive responses within 4 hours during business hours. Our 24/7 monitoring ensures critical issues are addressed immediately, regardless of time or day.',
    category: 'Support'
  },
  {
    id: '2',
    question: 'Do you offer on-site support or only remote assistance?',
    answer: 'We provide both remote and on-site support services. Remote support is available 24/7 for immediate assistance. On-site visits can be scheduled for installations, maintenance, complex troubleshooting, or situations requiring physical presence. Emergency on-site support is available within 2-4 hours in most metropolitan areas.',
    category: 'Support'
  },
  {
    id: '3',
    question: 'What information should I prepare before contacting support?',
    answer: 'Please have ready: your company name and contact information, a detailed description of the issue, affected systems or services, when the problem started, any error messages, and steps already taken. This helps our team diagnose and resolve issues more efficiently.',
    category: 'Support'
  },
  {
    id: '4',
    question: 'How do I schedule a free IT assessment?',
    answer: 'You can schedule a free assessment by filling out our consultation form, calling our office, or using the live chat feature. Our team will contact you within 24 hours to arrange a convenient time. The assessment typically takes 1-2 hours and includes a comprehensive review of your IT infrastructure, security posture, and recommendations.',
    category: 'Consultation'
  },
  {
    id: '5',
    question: 'What happens during a consultation?',
    answer: 'During the consultation, our IT strategists will discuss your business goals, current technology challenges, and future needs. We will review your existing infrastructure, identify improvement opportunities, and provide preliminary recommendations. There is no obligation, and you will receive a detailed assessment report within 48 hours.',
    category: 'Consultation'
  },
  {
    id: '6',
    question: 'Are consultations really free with no obligation?',
    answer: 'Yes, absolutely. Our initial consultation and IT assessment are completely free with no strings attached. We believe in demonstrating our expertise and value before asking for commitment. You will receive actionable insights regardless of whether you choose to work with us.',
    category: 'Consultation'
  },
  {
    id: '7',
    question: 'Which industries do you specialize in?',
    answer: 'We have deep expertise in healthcare, finance and banking, manufacturing, retail and e-commerce, education, and professional services. Our team understands industry-specific compliance requirements (HIPAA, PCI-DSS, SOX) and can tailor solutions to meet your sector\'s unique challenges.',
    category: 'Services'
  },
  {
    id: '8',
    question: 'Do you work with small businesses or only enterprises?',
    answer: 'We serve businesses of all sizes, from startups with 5 employees to enterprises with 500+ employees. Our solutions are scalable and can be customized to fit your budget and needs. We believe every business deserves enterprise-level IT support.',
    category: 'Services'
  }
];

export default function FAQSection() {
  const [openId, setOpenId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const categories = ['All', ...Array.from(new Set(faqs.map(faq => faq.category)))];

  const filteredFAQs = selectedCategory === 'All'
    ? faqs
    : faqs.filter(faq => faq.category === selectedCategory);

  const toggleFAQ = (id: string) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section className="py-16 lg:py-20 bg-muted">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-headline font-bold text-3xl lg:text-4xl text-primary mb-4">
              Frequently Asked Questions
            </h2>
            <p className="font-body text-lg text-muted-foreground">
              Find quick answers to common questions about our services and support.
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg font-body font-semibold text-sm transition-all duration-200 ${
                  selectedCategory === category
                    ? 'bg-brand-primary text-white shadow-card'
                    : 'bg-card text-muted-foreground hover:bg-background border border-border'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* FAQ List */}
          <div className="space-y-4">
            {filteredFAQs.map((faq) => (
              <div
                key={faq.id}
                className="bg-card rounded-lg border border-border overflow-hidden hover:shadow-card transition-all duration-200"
              >
                <button
                  onClick={() => toggleFAQ(faq.id)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-muted/50 transition-colors duration-200"
                >
                  <div className="flex-grow pr-4">
                    <h3 className="font-headline font-bold text-base lg:text-lg text-primary">
                      {faq.question}
                    </h3>
                  </div>
                  <div className={`flex-shrink-0 transform transition-transform duration-200 ${
                    openId === faq.id ? 'rotate-180' : ''
                  }`}>
                    <Icon name="ChevronDownIcon" size={24} className="text-brand-primary" />
                  </div>
                </button>

                {openId === faq.id && (
                  <div className="px-6 pb-6">
                    <div className="pt-4 border-t border-border">
                      <p className="font-body text-sm lg:text-base text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Still Have Questions CTA */}
          <div className="mt-12 bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 border border-brand-primary/20 rounded-lg p-8 text-center">
            <Icon name="QuestionMarkCircleIcon" size={48} className="text-brand-primary mx-auto mb-4" />
            <h3 className="font-headline font-bold text-2xl text-primary mb-2">
              Still Have Questions?
            </h3>
            <p className="font-body text-muted-foreground mb-6">
              Our team is here to help. Contact us directly and we&apos;ll provide personalized answers to your specific questions.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#consultation-form"
                className="flex items-center space-x-2 px-6 py-3 bg-brand-primary hover:bg-brand-primary/90 text-white rounded-lg font-cta font-semibold text-sm shadow-card transition-all duration-300 transform hover:scale-105"
              >
                <Icon name="ChatBubbleLeftRightIcon" size={18} variant="outline" />
                <span>Start a Conversation</span>
              </a>
              <a
                href="tel:+966552258277"
                className="flex items-center space-x-2 px-6 py-3 bg-card hover:bg-background border border-border text-primary rounded-lg font-cta font-semibold text-sm transition-all duration-200"
              >
                <Icon name="PhoneIcon" size={18} variant="outline" />
                <span>Call Us Now</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}